// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect } from 'react';
import clsx from 'clsx';
import { Portal } from '@cloudscape-design/component-toolkit/internal';
import { getBaseProps } from '../internal/base-component';
import { Transition } from '../internal/components/transition';
import { fireNonCancelableEvent } from '../internal/events';
import PopoverArrow from '../popover/arrow';
import PopoverBody from '../popover/body';
import PopoverContainer from '../popover/container';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';
export default function InternalTooltip({ content, getTrack, className, position = 'top', onEscape, referrerId, __internalRootRef, ...restProps }) {
    const baseProps = getBaseProps(restProps);
    const trackRef = React.useRef(null);
    // Update the ref with the current tracked element
    React.useEffect(() => {
        const element = getTrack();
        trackRef.current = element;
    }, [getTrack]);
    useEffect(() => {
        const controller = new AbortController();
        window.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                // Prevent any surrounding modals or dialogs from acting on this Esc.
                event.stopPropagation();
                fireNonCancelableEvent(onEscape);
            }
        }, {
            // The tooltip is often activated on mouseover, which means the focus can
            // be anywhere else on the page. Capture also means that this gets called
            // before any wrapper modals or dialogs can detect it and act on it.
            capture: true,
            signal: controller.signal,
        });
        return () => {
            controller.abort();
        };
    }, [onEscape]);
    return (React.createElement(Portal, null,
        React.createElement("div", { ...baseProps, className: clsx(testUtilStyles.root, styles.root, baseProps.className), ref: __internalRootRef, "data-awsui-referrer-id": referrerId, role: "tooltip" },
            React.createElement(Transition, { in: true }, () => (React.createElement(PopoverContainer, { getTrack: getTrack, size: "medium", fixedWidth: false, position: position, zIndex: 7000, arrow: position => React.createElement(PopoverArrow, { position: position }), hideOnOverscroll: true, className: className },
                React.createElement(PopoverBody, { dismissButton: false, dismissAriaLabel: undefined, onDismiss: undefined, header: undefined }, content)))))));
}
//# sourceMappingURL=internal.js.map